import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileDown, Eye, Trash2, Edit, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { generateEstimatePDF } from '@/utils/estimatePdf';
import EstimateViewModal from './EstimateViewModal';
import { useGlassContext } from '@/context/GlassContext';
import { useEstimate } from '@/contexts/EstimateContext';
import { getImagesForEstimate, deleteImagesForEstimate, EstimateImage } from '@/services/imageService';

// Enable or disable debug logging
const DEBUG = true;

// Helper function for debug logging
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log('[SavedEstimatesTab]', ...args);
  }
};

// Define the SavedEstimate type to match what's stored in localStorage
interface SavedEstimate {
  id: string;
  name: string;
  date: string;
  items: any[];
  // Add other fields that might be in the saved estimates
  companyName?: string;
  measurementOptionsSets?: any[];
  customerInfo?: any;
  settings?: {
    taxRate: number;
    surchargeRate: number;
    markupRate: number;
    shippingRate: number;
    otherFee: number;
    roundUpGlassSize?: boolean;
  };
  // Image references (actual images are stored in IndexedDB)
  imageReferences?: {
    id: string;
    fileName: string;
    description: string;
    timestamp: number;
  }[];
}

const SavedEstimatesTab: React.FC = () => {
  const [savedEstimates, setSavedEstimates] = useState<SavedEstimate[]>([]);
  const [selectedEstimate, setSelectedEstimate] = useState<SavedEstimate | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [estimateImages, setEstimateImages] = useState<EstimateImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const { glassTypes } = useGlassContext();
  const { loadEstimate } = useEstimate();

  // Load saved estimates from localStorage
  useEffect(() => {
    const loadSavedEstimates = () => {
      try {
        debugLog('Loading saved estimates from localStorage');
        const savedEstimatesJSON = localStorage.getItem('savedEstimates');
        if (savedEstimatesJSON) {
          const estimates = JSON.parse(savedEstimatesJSON);
          debugLog('Loaded estimates:', estimates);
          setSavedEstimates(estimates);
        } else {
          debugLog('No saved estimates found in localStorage');
        }
      } catch (error) {
        console.error('Error loading saved estimates:', error);
        toast.error('Failed to load saved estimates');
      }
    };

    loadSavedEstimates();
  }, []);

  // Handle deleting an estimate
  const handleDeleteEstimate = async (id: string) => {
    try {
      // Delete associated images from IndexedDB
      await deleteImagesForEstimate(id);

      // Filter out the estimate to delete
      const updatedEstimates = savedEstimates.filter(estimate => estimate.id !== id);

      // Update localStorage
      localStorage.setItem('savedEstimates', JSON.stringify(updatedEstimates));

      // Update state
      setSavedEstimates(updatedEstimates);

      // Show success message
      toast.success('Estimate and associated images deleted successfully');
    } catch (error) {
      console.error('Error deleting estimate:', error);
      toast.error('Failed to delete estimate');
    }
  };

  // Handle exporting an estimate as PDF
  const handleExportPDF = async (estimate: SavedEstimate) => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Preparing PDF...');

      // Make sure the estimate has settings
      const estimateWithSettings = {
        ...estimate,
        settings: estimate.settings || {
          taxRate: 8.25,
          surchargeRate: 0,
          markupRate: 0,
          shippingRate: 15,
          otherFee: 0,
          roundUpGlassSize: false // Add default for roundUpGlassSize
        }
      };

      // Ensure roundUpGlassSize is included in settings
      if (estimateWithSettings.settings && estimateWithSettings.settings.roundUpGlassSize === undefined) {
        estimateWithSettings.settings.roundUpGlassSize = false;
      }

      // Load images from IndexedDB
      let images: EstimateImage[] = [];
      try {
        debugLog(`Loading images for estimate ${estimate.id} from IndexedDB`);
        images = await getImagesForEstimate(estimate.id);
        debugLog(`Successfully loaded ${images.length} images for PDF export`);

        // Log image details for debugging
        if (images.length > 0) {
          debugLog('Image details:', images.map(img => ({
            id: img.id,
            fileName: img.fileName,
            hasDataUrl: !!img.dataUrl,
            dataUrlLength: img.dataUrl ? img.dataUrl.length : 0,
            estimateId: img.estimateId
          })));
        } else if (estimate.imageReferences && estimate.imageReferences.length > 0) {
          debugLog(`Warning: Found ${estimate.imageReferences.length} image references but no actual images in IndexedDB`);
        }
      } catch (imageError) {
        console.error('Error loading images for PDF:', imageError);
        toast.error('Failed to load images for the PDF. The PDF will be generated without images.');
        // Continue without images
      }

      // Log the estimate with settings for debugging
      debugLog('Exporting PDF with settings:', estimateWithSettings);
      debugLog(`Including ${images.length} images in the PDF`);

      // Generate PDF with images
      const doc = generateEstimatePDF(estimateWithSettings, images);

      // Save the PDF
      doc.save(`${estimate.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);

      // Dismiss loading toast and show success message
      toast.dismiss(loadingToast);
      toast.success(`PDF has been generated and downloaded with ${images.length} images`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  // Handle viewing an estimate
  const handleViewEstimate = async (estimate: SavedEstimate) => {
    debugLog('Viewing estimate:', estimate);
    setSelectedEstimate(estimate);
    setLoadingImages(true);

    try {
      // Load images from IndexedDB
      debugLog(`Loading images for estimate ${estimate.id} from IndexedDB`);
      const images = await getImagesForEstimate(estimate.id);
      setEstimateImages(images);
      debugLog(`Successfully loaded ${images.length} images for estimate ${estimate.id}`);

      // Log image details for debugging
      if (images.length > 0) {
        debugLog('Image details:', images.map(img => ({
          id: img.id,
          fileName: img.fileName,
          hasDataUrl: !!img.dataUrl,
          dataUrlLength: img.dataUrl ? img.dataUrl.length : 0,
          estimateId: img.estimateId
        })));
      } else if (estimate.imageReferences && estimate.imageReferences.length > 0) {
        debugLog(`Warning: Found ${estimate.imageReferences.length} image references but no actual images in IndexedDB`);
      }
    } catch (error) {
      console.error('Error loading images:', error);
      setEstimateImages([]);
      toast.error('Failed to load images for this estimate');
    } finally {
      setLoadingImages(false);
    }

    setIsViewModalOpen(true);
    toast.info(`Viewing estimate: ${estimate.name}`);
  };

  // Handle editing an estimate
  const handleEditEstimate = async (estimate: SavedEstimate) => {
    debugLog('Editing estimate:', estimate);

    // Show loading toast
    const loadingToast = toast.loading('Loading estimate for editing...');

    try {
      // Load images from IndexedDB
      debugLog(`Loading images for estimate ${estimate.id} from IndexedDB`);
      const images = await getImagesForEstimate(estimate.id);
      debugLog(`Successfully loaded ${images.length} images for estimate ${estimate.id}`);

      // Log image details for debugging
      if (images.length > 0) {
        debugLog('Image details:', images.map(img => ({
          id: img.id,
          fileName: img.fileName,
          hasDataUrl: !!img.dataUrl,
          dataUrlLength: img.dataUrl ? img.dataUrl.length : 0,
          estimateId: img.estimateId
        })));
      } else if (estimate.imageReferences && estimate.imageReferences.length > 0) {
        debugLog(`Warning: Found ${estimate.imageReferences.length} image references but no actual images in IndexedDB`);
      }

      // Load the estimate into the context
      await loadEstimate(
        estimate.items,
        estimate.name,
        estimate.id,
        estimate.customerInfo || {
          companyName: '',
          companyAddress: '',
          companyCity: '',
          companyState: '',
          companyZip: '',
          contactName: '',
          contactPhone: '',
          projectName: '',
          projectAddress: '',
          projectCity: '',
          projectState: '',
          projectZip: '',
          useProjectAddress: false
        },
        images // Pass the loaded images to the context
      );

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Switch to the Estimate tab
      const estimateTab = document.querySelector('[data-value="estimate"]') as HTMLElement;
      if (estimateTab) {
        estimateTab.click();
        toast.success(`Loaded estimate "${estimate.name}" for editing with ${images.length} images`);
      } else {
        toast.error('Could not switch to Estimate tab');
      }
    } catch (error) {
      console.error('Error loading images for editing:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to load all images for this estimate');
    }
  };

  // Handle modal close
  const handleModalOpenChange = (open: boolean) => {
    setIsViewModalOpen(open);
    if (!open) {
      // Optional: Clear the selected estimate when the modal is closed
      // setSelectedEstimate(null);
    }
  };

  // Format date string
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Convert SavedEstimate to the format expected by EstimateViewModal
  const convertToEstimateFormat = (savedEstimate: SavedEstimate) => {
    if (!savedEstimate) return null;

    // Debug log the saved estimate
    debugLog('Converting saved estimate to format expected by EstimateViewModal:', savedEstimate);

    // Create a properly formatted Estimate object
    return {
      id: savedEstimate.id,
      name: savedEstimate.name,
      items: savedEstimate.items.map(item => {
        // Ensure we preserve the complete glass type object with all its properties
        let glassTypeObj = null;

        if (typeof item.glassType === 'object' && item.glassType !== null) {
          // Make sure we have all the required properties for cost calculation
          glassTypeObj = {
            ...item.glassType,
            // Ensure these properties exist for cost calculations
            costPerSqFt: item.glassType.costPerSqFt || item.glassType.price || 0,
            weightPerSqFt: item.glassType.weightPerSqFt || item.glassType.lbsPerSqFt || 0
          };

          debugLog('Preserved glass type object:', glassTypeObj);
        }

        return {
          ...item,
          // Ensure required properties exist
          id: item.id || `temp-${Math.random().toString(36).substring(2, 9)}`,
          quantity: item.quantity || 1,
          measurementOptionsId: item.measurementOptionsId || '',
          // Make sure the glass type is preserved with all necessary properties
          glassType: glassTypeObj
        };
      }),
      created: new Date(savedEstimate.date),
      companyName: savedEstimate.companyName || 'ServicePane',
      measurementOptionsSets: savedEstimate.measurementOptionsSets || [],
      // Include customer info if available
      customerInfo: savedEstimate.customerInfo || {},
      // Include settings if available, or use defaults
      settings: savedEstimate.settings || {
        taxRate: 8.25,
        surchargeRate: 0,
        markupRate: 0,
        shippingRate: 15,
        otherFee: 0,
        roundUpGlassSize: false
      },
      // Include loaded images
      images: estimateImages
    };
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Saved Estimates</h1>

      {savedEstimates.length === 0 ? (
        <Card className="bg-[#1e293b] text-white border-[#4b5563]">
          <CardContent className="pt-6">
            <div className="text-center py-8 text-muted-foreground">
              No saved estimates found. Create and save an estimate from the Estimate tab.
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#1e293b] text-white border-[#4b5563]">
          <CardHeader>
            <CardTitle className="text-[#60a5fa]">Your Saved Estimates</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedEstimates.map((estimate) => (
                  <TableRow key={estimate.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        {estimate.imageReferences && estimate.imageReferences.length > 0 && (
                          <div className="mr-2" title={`${estimate.imageReferences.length} images attached`}>
                            <ImageIcon className="h-4 w-4 text-blue-400" />
                          </div>
                        )}
                        {estimate.name}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(estimate.date)}</TableCell>
                    <TableCell>{estimate.items.length} items</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewEstimate(estimate)}
                          className="h-8 w-8 text-blue-500 hover:text-blue-700 hover:bg-blue-100/10"
                          title="View Estimate"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditEstimate(estimate)}
                          className="h-8 w-8 text-yellow-500 hover:text-yellow-700 hover:bg-yellow-100/10"
                          title="Edit Estimate"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleExportPDF(estimate)}
                          className="h-8 w-8 text-green-500 hover:text-green-700 hover:bg-green-100/10"
                          title="Download PDF"
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEstimate(estimate.id)}
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-100/10"
                          title="Delete Estimate"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Estimate View Modal */}
      <EstimateViewModal
        estimate={selectedEstimate ? convertToEstimateFormat(selectedEstimate) : null}
        open={isViewModalOpen}
        onOpenChange={handleModalOpenChange}
      />
    </div>
  );
};

export default SavedEstimatesTab;
